import type {
  NotificationDeliveryItem,
  NotificationDeliveryList as NotificationDeliveryListDto,
  NotificationDeliveryListInput,
  ResendNotificationInput,
} from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { ResendNotificationDialog } from '@/features/notification/resend';
import { Button, cn, EmptyState, Pagination, tableStyles } from '@/shared/ui';
import {
  type DeliveryFilters,
  deliveryQueryOf,
  filtersAfterResend,
  filtersOfQuery,
  isDateRangeReversed,
  parseRecipientEmail,
} from './delivery-list-query';
import { NotificationDeliveryFilters } from './notification-delivery-filters';
import { NotificationDeliveryRow } from './notification-delivery-row';

interface NotificationDeliveryListProps {
  readonly data?: NotificationDeliveryListDto;
  readonly initialQuery?: NotificationDeliveryListInput;
  readonly onQueryChange: (query: NotificationDeliveryListInput) => void;
  /**
   * 재발송 쓰기 훅(useResendNotification). 성공하면 목록이 다시 읽히고, 새 "대기" 행이 지금 조건에 숨으면(대기가 아닌 결과 필터·2쪽 이후)
   * 위젯이 결과 필터를 풀고 1쪽으로 조건을 바꾼다(`onQueryChange`).
   */
  readonly onResend: (input: ResendNotificationInput) => Promise<unknown>;
  /** [새로고침] — 지금 조건 그대로 목록을 다시 읽는다(발송 이력 캐시 무효화). 주지 않으면 버튼이 없다. */
  readonly onRefresh?: () => void;
  readonly pending?: boolean;
  readonly error?: string | null;
}

const DEFAULT_QUERY: NotificationDeliveryListInput = { page: 1, limit: 20 };
const INVALID_EMAIL_MESSAGE = '올바른 이메일 주소를 입력해 주세요.';
const REVERSED_DATE_MESSAGE = '시작일이 종료일보다 늦습니다. 기간을 바로잡아 주세요.';

const COLUMNS = [
  { label: '발송 일시' },
  { label: '종류' },
  { label: '수신 이메일' },
  { label: '참조' },
  { label: '결과' },
  { label: '사유' },
  { label: '처리', className: 'text-right' },
] as const;

/**
 * 어드민 알림 발송 이력(알림 설계 §6-1). 구성: 도구줄(종류·결과·기간·수신 이메일), 흰 표 카드, 페이지 이동, 재발송 대화상자.
 * 처음 읽는 중·실패·빈 결과는 카드 안 가운데 안내(EmptyState)로 보인다. 조건을 바꿔 다시 읽는 동안에는 받은 표를 그대로 두고
 * 읽는 중임만 알린다. 수신 주소가 보이는 화면이라 서버가 조회마다 관리자 접속기록을 남긴다. 기간이 거꾸로면 안내만 하고 조회를 보내지
 * 않는다(바로잡으면 그때 보낸다).
 */
export const NotificationDeliveryList = ({
  data,
  initialQuery,
  onQueryChange,
  onResend,
  onRefresh,
  pending = false,
  error = null,
}: NotificationDeliveryListProps): ReactElement => {
  const start = initialQuery ?? DEFAULT_QUERY;
  const [filters, setFilters] = useState<DeliveryFilters>(() => filtersOfQuery(start));
  const [email, setEmail] = useState(start.recipientEmail ?? '');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [target, setTarget] = useState<NotificationDeliveryItem | null>(null);

  const change = (next: DeliveryFilters, page: number): void => {
    setFilters(next);
    if (isDateRangeReversed(next)) return;
    onQueryChange(deliveryQueryOf(next, page, start.limit));
  };
  const resend = async (input: ResendNotificationInput): Promise<unknown> => {
    const result = await onResend(input);
    const next = filtersAfterResend(filters);
    const page = data?.meta.page ?? start.page;
    if (next !== filters || page !== 1) change(next, 1);
    return result;
  };
  const submitEmail = (): void => {
    const parsed = parseRecipientEmail(email);
    if (parsed.kind === 'INVALID') {
      setEmailError(INVALID_EMAIL_MESSAGE);
      return;
    }
    setEmailError(null);
    change({ ...filters, recipientEmail: parsed.kind === 'VALID' ? parsed.email : null }, 1);
  };
  // 기록은 있는데 보던 쪽이 범위를 벗어나 비었으면(재발송·정리로 건수가 바뀐 경우) "기록 없음"이 아니라 범위 밖이다.
  const outOfRange =
    data !== undefined &&
    data.items.length === 0 &&
    data.meta.totalPages > 0 &&
    data.meta.page > data.meta.totalPages;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <NotificationDeliveryFilters
          kind={filters.kind}
          status={filters.status}
          fromDate={filters.fromDate}
          toDate={filters.toDate}
          email={email}
          emailError={emailError}
          dateError={isDateRangeReversed(filters) ? REVERSED_DATE_MESSAGE : null}
          onKind={(kind) => change({ ...filters, kind }, 1)}
          onStatus={(status) => change({ ...filters, status }, 1)}
          onFromDate={(fromDate) => change({ ...filters, fromDate }, 1)}
          onToDate={(toDate) => change({ ...filters, toDate }, 1)}
          onEmail={setEmail}
          onSubmit={submitEmail}
        />
        {onRefresh ? (
          <Button type="button" variant="outline" className="h-10 px-5" onClick={onRefresh}>
            새로고침
          </Button>
        ) : null}
        {pending && data ? (
          <p role="status" className="ml-auto text-sm text-muted-foreground">
            불러오는 중…
          </p>
        ) : null}
      </div>

      {data === undefined ? (
        <div className="mt-5">
          {error ? <EmptyState isError>{error}</EmptyState> : <EmptyState>불러오는 중…</EmptyState>}
        </div>
      ) : (
        <>
          {error ? (
            <p role="alert" className="mt-4 text-sm text-destructive">
              {error}
            </p>
          ) : null}

          {outOfRange ? (
            <div className="mt-5 flex flex-col items-center gap-4">
              <EmptyState>페이지 범위를 벗어났습니다.</EmptyState>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => change(filters, data.meta.totalPages)}
              >
                마지막 페이지로
              </Button>
            </div>
          ) : data.items.length === 0 ? (
            <div className="mt-5">
              <EmptyState>조건에 맞는 알림 기록이 없습니다.</EmptyState>
            </div>
          ) : (
            <div className={cn('mt-5', tableStyles.card)}>
              <table className={tableStyles.table}>
                <thead className={tableStyles.head}>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.label}
                        className={cn(tableStyles.th, 'className' in column && column.className)}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((delivery) => (
                    <NotificationDeliveryRow
                      key={delivery.id}
                      delivery={delivery}
                      onResend={setTarget}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {outOfRange ? null : (
            <Pagination
              className="mt-5 justify-center"
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              onPageChange={(page) => change(filters, page)}
            />
          )}
        </>
      )}

      {target === null ? null : (
        <ResendNotificationDialog
          key={target.id}
          delivery={target}
          onSubmit={resend}
          onClose={() => setTarget(null)}
        />
      )}
    </>
  );
};
